import invariant from 'tiny-invariant';
import {
  AddToCartEventData,
  APP_NAME,
  AppStartEventData,
  DataSchema,
  ImageUploadFailedEventData,
  LoginEventData,
  LoginFailEventData,
  NewProjectEventData,
  RegisterEventData,
  RegisterFailEventData,
  RenderFailedEventData,
  UploadFailedEventData,
  EventSchema,
} from './ct-event-schema';
import { analyticsBase } from '../config';
import { deobfuscateProjectId } from './projectId';
import { store } from '../index';
import { logCTEventsToGA } from './TagManager';

//
// # References
//
// ## Audit Logger
//
// AWS infra for API Gateway -> SQS:
// https://github.com/blurb/audit-logger/tree/master
//
// We hit the API gateway endpoint via a blurb.com CNAME record.
//
// ## BookWright
//
// Most of the event structure is logic is here:
// https://github.com/blurb/bookwright/blob/master/src/analytics/BlurbAnalytics.cpp
//
// Actual POST happens here:
// https://github.com/blurb/bookwright/blob/master/src/network/APIManager.cpp
//

// How long to collect events before uploading (in millis)
const QUEUE_WINDOW = 5000;

// TODO quietly disable for now if not connected to PDX
let tempDisabled = false;

/** @type unknown[] */
let queue = [];

/** @type null | number */
let timeout = null;

let initialized = false;

export function initializeCtEvents() {
  invariant(!initialized, 'CT Events already initialized');
  initialized = true;
}

/** @param {ReturnType<typeof EventSchema.safeParse>} safeParseResult */
function queueEventData(safeParseResult) {
  if (!safeParseResult.success) {
    console.error('Failed to parse event data:', safeParseResult.error);
    return;
  }
  queue.push({
    data: safeParseResult.data,
    timestamp: new Date().toISOString(),
  });
  if (timeout !== null) {
    return;
  }
  timeout = window.setTimeout(flush, QUEUE_WINDOW);
}

async function flush() {
  invariant(timeout);
  timeout = null;

  invariant(__COMMIT_HASH__, '__COMMIT_HASH__ not set');
  invariant(__VERSION__, '__VERSION__ not set');

  invariant(queue.length);

  const projectData = tryGetProjectData();
  const data = DataSchema.parse({
    branch: 'production',
    build: '0',
    commit: __COMMIT_HASH__,
    context: APP_NAME,
    events: queue,
    instance_id: 'TODO',
    project_id: projectData?.projectId ?? null,
    version: __VERSION__,
  });

  queue = [];

  const params = new URLSearchParams({
    session_id: 'TODO',
    api_key: 'TODO',
    // https://github.com/blurb/bookwright/blob/38421d172b005a69c3a18622889c0ca829200770/src/network/APIManager.cpp#L170
    sig: '_a143fa42167648082b6ae8327e76fb0fe6ef0b25',
    data: JSON.stringify(data),
  });

  if (tempDisabled) {
    console.debug('Ignoring event(s)...', params);
    return;
  }

  if (!initialized) {
    if (__WEBPACK_SERVE__) {
      console.debug('CT Event:', data);
    }
    return;
  }

  /** @type Response */
  let response;
  try {
    response = await fetch(analyticsBase, {
      method: 'post',
      body: params,
    });
  } catch {
    console.warn('Failed to post CT event(s), disabling...');
    tempDisabled = true;
    return;
  }

  if (!response.ok || response.status !== 200) {
    console.error('TODO handle CT events error', response);
  }

  //log ct events to GA too
  try {
    logCTEventsToGA(data.events);
  } catch (e) {
    console.error('Failed to log ct events to GA', e);
  }
}

export function emitStartupEvent() {
  queueEventData(
    AppStartEventData.safeParse({
      ...getDefaultEventData(),
      type: 'App Start',
      userAgent: window.navigator.userAgent,
    }),
  );
}

export function emitLoginEvent(username) {
  queueEventData(
    LoginEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Login Successful',
      login: username,
    }),
  );
}

export function emitLoginFailEvent(reason, username) {
  queueEventData(
    LoginFailEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Login Failed',
      loginFail: {
        reason,
        username,
      },
    }),
  );
}

export function emitRegisterEvent(username) {
  queueEventData(
    RegisterEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Registration Successful',
      login: username,
    }),
  );
}

export function emitRegisterFailEvent(reason, username) {
  queueEventData(
    RegisterFailEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Registration Failed',
      registerFail: {
        reason,
        username,
      },
    }),
  );
}

export function emitNewProjectEvent(blurbID) {
  queueEventData(
    NewProjectEventData.safeParse({
      ...getDefaultEventData(),
      type: 'New Project',
      blurbID,
    }),
  );
}

export function emitImageUploadFailedEvent(
  imageID,
  thumbPath,
  imageDetails,
  error,
) {
  queueEventData(
    ImageUploadFailedEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Image Upload Failed',
      imageID,
      thumbPath,
      imageDetails,
      error,
    }),
  );
}

export function emitAddToCartEvent(source) {
  queueEventData(
    AddToCartEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Add To Cart',
      source,
    }),
  );
}

export function emitRenderFailedEvent(renderJobPK, status, error) {
  queueEventData(
    RenderFailedEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Render Failed',
      renderJobPK,
      status,
      error,
    }),
  );
}

export function emitUploadFailedEvent(renderJobPK, status, error) {
  queueEventData(
    UploadFailedEventData.safeParse({
      ...getDefaultEventData(),
      type: 'Upload Failed',
      renderJobPK,
      status,
      error,
    }),
  );
}

function tryGetProjectData() {
  const match = window.location.pathname.match(/\/editor\/([^/]+)/);
  if (!match) {
    return null;
  }
  try {
    const state = store.getState();
    const projectTitle = state.project?.present?.metadata.get('name') ?? null;
    const createdDate =
      state.project?.present?.metadata.get('createdDate') ?? null;
    const projectId = deobfuscateProjectId(match[1]);
    return { projectId, projectTitle, createdDate };
  } catch (_e) {
    // TODO We're not necessarily on the project page.
    // Should find a better way of getting project ID here
    // that doesn't involve ignoring a deobfuscation error.
    return null;
  }
}

/** @returns {Pick<typeof TypeSchema, 'source' | 'userID'>} */
function getDefaultEventData() {
  const user = tryGetUserData();
  const projectData = tryGetProjectData();
  return {
    userID: user.accountID,
    username: user.username,
    title: projectData?.projectTitle ?? null,
    projectStartDateUTC: projectData?.createdDate ?? null,
    source: window.location?.pathname ?? null,
  };
}

function tryGetUserData() {
  try {
    const state = store.getState();
    const accountID = state.user?.get('accountID') ?? null;
    const username = state.user?.get('blurbUsername') ?? null;
    return { accountID, username };
  } catch (_e) {
    return null;
  }
}
